/**
 * A4 „Geld nach Alter" (Workshop-Lücken 29.10.2026, Plan `.claude/plans/2026-10-29-workshop-luecken.md` Paket U, Q2 Till):
 * je Bestellmonat, was die Anfos bis zum Alter 30/60/90/180 Tage tatsächlich gezahlt hatten (je Anfo, netto), gegen den
 * vertretbaren Einkaufspreis je Anfo (CPA-Wert bei Bestellung). Nur beobachtete Zahlungsstände (`plx_payment_snapshot`,
 * seit 15.05.2026), keine Rekonstruktion; eine Zelle steht nur, wenn ALLE Anfos des Monats beobachtbar sind, sonst grau
 * „bekannt von". Backend `/api/auswertungen/umsatz/geld-nach-alter` (`geldNachAlterService.ts`).
 */
import { FC, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte } from '../../../components/auswertungen/SortableTable';
import { BefundZeile, Hinweis, int, Laden, spanne, useBreitePx, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { formatMonat } from '../../../helpers/auswertungenData';
import { formatEur, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchGeldNachAlter, type GeldMonat, type GeldZelle } from '../../../helpers/umsatzData';
import { Kartenkopf } from './teile';

const euro = (v: number | null | undefined) => (v === null || v === undefined ? '—' : formatEur(v, 0));
const datumDe = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
/** Unter dieser Kartenbreite steht „Anfos" und „erreicht ab" als Unterzeile des Monats (1.024 px: Karte 654 px, sieben
 *  Spalten liefen 81 px über und versteckten „erreicht ab" hinter der Bildlaufleiste, gemessen 29.10.). */
const SCHMAL_UNTER = 760;
/** Darunter (390 px, Karte ~270 px) wandert auch der Einkaufspreis in die Unterzeile und die Zellen werden kompakt —
 *  sonst lief die Tabelle 107 px über und Tag 180 war nur per Querscrollen zu sehen. */
const SEHR_SCHMAL_UNTER = 420;

/** „1 Monat" / „4 Monaten" im Satz. */
const monateWort = (k: number) => `${k.toLocaleString('de-DE')} ${k === 1 ? 'Monat' : 'Monaten'}`;

/**
 * Befund aus den Monaten, in denen Tag 30 UND Tag 60 vollständig gemessen sind (rein, für den Test exportiert). Audit C6
 * (30.10.2026): eine klare Aussage vorn — ab welchem Alter der Einkaufspreis in ALLEN dieser Monate eingespielt war.
 * Bewusst kein „dauerhaft": Rückbuchungen können den Stand später senken (Codex-Prüfung 7).
 */
export function geldBefund(rows: GeldMonat[]): string | null {
  const z = (r: GeldMonat, h: number) => r.zellen.find((x) => x.h === h);
  const beide = rows.filter((r) => z(r, 30)?.ueber_cpa !== null && z(r, 30)?.ueber_cpa !== undefined && z(r, 60)?.ueber_cpa !== null && z(r, 60)?.ueber_cpa !== undefined);
  if (beide.length === 0) return null;
  const n = beide.length;
  const k30 = beide.filter((r) => z(r, 30)?.ueber_cpa).length;
  const k60 = beide.filter((r) => z(r, 60)?.ueber_cpa).length;
  const monate = n === 1 ? formatMonat(beide[0].month) : `${formatMonat(beide[0].month)}–${formatMonat(beide[n - 1].month)}`;
  const alle = n === 1 ? `im einzigen voll gemessenen Bestellmonat (${monate})` : `in allen ${n} voll gemessenen Bestellmonaten (${monate})`;
  if (k30 === n) return `Nach 30 Tagen eingespielt — ${alle} hatten die Anfos bei Tag 30 im Schnitt ihren vertretbaren Einkaufspreis bezahlt.`;
  if (k60 === n) {
    return `Nach 60 Tagen eingespielt — ${alle} hatten die Anfos bei Tag 60 im Schnitt ihren vertretbaren Einkaufspreis bezahlt, bei Tag 30 erst in ${k30 === 0 ? 'keinem' : `${k30} von ${n}`}.`;
  }
  return `Nach 60 Tagen noch nicht überall eingespielt — von ${n} voll gemessenen Bestellmonaten (${monate}) hatten die Anfos den vertretbaren Einkaufspreis bei Tag 60 in ${monateWort(k60)} bezahlt, bei Tag 30 in ${monateWort(k30)}.`;
}

export type ZellenStand = 'wert' | 'offen' | 'unvollstaendig';

/** ISO-Tag + h Tage. */
const plusTage = (iso: string, h: number): string => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + h);
  return d.toISOString().slice(0, 10);
};

/**
 * Warum eine Zelle keinen Wert hat (Audit C6, Codex-Prüfung 7): „noch offen" nur, wenn Anfos des Monats das Alter h noch
 * nicht erreicht haben (letzter Bestelltag + h nach dem jüngsten Zahlungsstand). Fällt der Zieltag vor den ersten
 * gespeicherten Stand oder fehlt der Stand einzelner Anfos, wird die Zelle nie vollständig — „nicht vollständig gemessen".
 */
export function zellenStand(month: string, z: GeldZelle, snapshotAb: string, stand: string): ZellenStand {
  if (z.wert !== null) return 'wert';
  const erster = `${month}-01`;
  const letzter = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10);
  if (plusTage(erster, z.h) < snapshotAb) return 'unvollstaendig';
  if (plusTage(letzter, z.h) > stand) return 'offen';
  return 'unvollstaendig';
}

/** Text der grauen Zelle: „noch offen (2 von 279 gemessen)", „nicht vollständig gemessen (144 von 323)". Schmal bricht er um. */
export function zellenText(stand: ZellenStand, z: GeldZelle): string {
  const nN = `${int(z.n)} von ${int(z.N)}`;
  if (stand === 'offen') return z.n === 0 ? 'noch offen' : `noch offen (${nN} gemessen)`;
  return z.n === 0 ? 'nicht gemessen' : `nicht vollständig gemessen (${nN})`;
}

const Zelle: FC<{ z: GeldZelle | undefined; cpa: number | null; month: string; snapshotAb: string; stand: string; kompakt?: boolean }> = ({
  z,
  cpa,
  month,
  snapshotAb,
  stand,
  kompakt,
}) => {
  if (!z) return <>—</>;
  const st = zellenStand(month, z, snapshotAb, stand);
  if (st !== 'wert')
    return (
      <Tooltip
        arrow
        placement="top"
        title={
          st === 'offen'
            ? `Noch offen: Anfos dieses Bestellmonats haben das Alter von ${z.h} Tagen noch nicht erreicht. Bisher ist der Stand für ${int(z.n)} von ${int(z.N)} Anfos gemessen; der Schnitt steht, sobald alle so alt sind.`
            : `Nicht vollständig gemessen: Für ${int(z.n)} von ${int(z.N)} Anfos ist der Zahlungsstand an diesem Tag bekannt (gespeichert erst seit ${datumDe(snapshotAb)}, einzelne Stände fehlen). Der Schnitt stünde schief, deshalb kein Wert — diese Zelle wird nicht mehr vollständig.`
        }
      >
        <Box component="span" sx={{ color: 'text.disabled', fontSize: kompakt ? 10.5 : 12, cursor: 'help', display: 'inline-block', maxWidth: 150, lineHeight: 1.25, whiteSpace: 'normal' }}>
          {zellenText(st, z)}
        </Box>
      </Tooltip>
    );
  const ueber = z.ueber_cpa === true;
  return (
    <Tooltip
      arrow
      placement="top"
      title={`${euro(z.wert)} je Anfo bis Tag ${z.h} (alle ${int(z.N)} Anfos). ${cpa === null ? 'Kein Einkaufspreis bekannt.' : ueber ? `Mindestens der Einkaufspreis von ${euro(cpa)}.` : `Unter dem Einkaufspreis von ${euro(cpa)}.`}`}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          px: kompakt ? 0.25 : 0.75,
          fontSize: kompakt ? 12 : undefined,
          borderRadius: '4px',
          cursor: 'help',
          fontWeight: ueber ? 600 : 400,
          backgroundColor: ueber ? 'rgba(46, 125, 50, 0.14)' : 'transparent',
          color: ueber ? 'success.dark' : 'text.primary',
        }}
      >
        {euro(z.wert)}
      </Box>
    </Tooltip>
  );
};

const erreichtText = (r: GeldMonat): string =>
  r.cpa_erreicht_ab ? `${r.cpa_erreicht_ab.spaetestens ? 'spätestens ' : ''}Tag ${r.cpa_erreicht_ab.h}` : '—';

const GeldNachAlterKarte: FC = () => {
  const { data, fehler } = useLaden(fetchGeldNachAlter);
  const [messRef, breite] = useBreitePx();
  const schmal = breite !== null && breite < SCHMAL_UNTER;
  const sehrSchmal = breite !== null && breite < SEHR_SCHMAL_UNTER;
  const alter = data?.alter ?? [30, 60, 90, 180];
  const zeilen = useMemo(() => [...(data?.rows ?? [])].reverse(), [data]);
  const ohneCpa = (data?.rows ?? []).reduce((s, r) => s + r.ohne_cpa, 0);
  // Jüngster Zahlungsstand (Tag): bis dahin ist gemessen; danach liegende Zieltage sind „noch offen“.
  const stand = data?.snapshot_stand ?? data?.as_of ?? '';

  const spalten: Spalte<GeldMonat>[] = [
    {
      key: 'monat',
      label: sehrSchmal ? 'Monat' : 'Bestellmonat',
      sortValue: (r) => r.month,
      render: (r) => (
        <Box sx={{ lineHeight: 1.3 }}>
          {formatMonat(r.month)}
          {schmal && (
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'normal' }}>
              {int(r.anfos)} Anfos{sehrSchmal ? ` · Einkaufspreis ${euro(r.cpa_je_anfo)}` : ''} ·{' '}
              {r.cpa_erreicht_ab ? `erreicht ${erreichtText(r)}` : 'noch nicht erreicht'}
            </Box>
          )}
        </Box>
      ),
    },
    ...(schmal
      ? []
      : [
          {
            key: 'anfos',
            label: TERMS.anfos.label,
            tip: 'Nicht vorläufige Anfos des Bestellmonats = Nenner jeder Zelle (Anfos ohne Zahlung zählen mit 0 €).',
            align: 'right' as const,
            sortValue: (r: GeldMonat) => r.anfos,
            render: (r: GeldMonat) => int(r.anfos),
          },
        ]),
    ...alter.map(
      (h): Spalte<GeldMonat> => ({
        key: `h${h}`,
        label: `Tag ${h}`,
        einheit: h === alter[0] ? 'gezahlt je Anfo' : undefined,
        tip: TERMS.geldNachAlter.tip,
        align: 'right',
        sortValue: (r) => r.zellen.find((z) => z.h === h)?.wert ?? null,
        render: (r) => (
          <Zelle z={r.zellen.find((z) => z.h === h)} cpa={r.cpa_je_anfo} month={r.month} snapshotAb={data?.snapshot_ab ?? ''} stand={stand} kompakt={sehrSchmal} />
        ),
      }),
    ),
    ...(sehrSchmal
      ? []
      : [
          {
            key: 'cpa',
            label: schmal ? 'Einkaufspreis' : TERMS.cpaJeAnfoBestellung.label,
            einheit: TERMS.cpaJeAnfoBestellung.einheit,
            tip: TERMS.cpaJeAnfoBestellung.tip,
            align: 'right' as const,
            sortValue: (r: GeldMonat) => r.cpa_je_anfo,
            render: (r: GeldMonat) => (
              <Box sx={{ lineHeight: 1.25 }}>
                {euro(r.cpa_je_anfo)}
                {r.ohne_cpa > 0 && (
                  <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
                    {int(r.ohne_cpa)} ohne CPA-Wert
                  </Box>
                )}
              </Box>
            ),
          },
        ]),
    ...(schmal
      ? []
      : [
          {
            key: 'erreicht',
            label: TERMS.cpaBudgetErreicht.label,
            tip: TERMS.cpaBudgetErreicht.tip,
            align: 'right' as const,
            sortValue: (r: GeldMonat) => (r.cpa_erreicht_ab ? r.cpa_erreicht_ab.h : null),
            render: (r: GeldMonat) => erreichtText(r),
          },
        ]),
  ];

  return (
    <ChartCard
      title="Wann die Anfos ihren Einkaufspreis bezahlt haben"
      anker="geld-nach-alter"
      howToRead="Je Bestellmonat: was die Anfos bis zum Alter von 30, 60, 90 und 180 Tagen tatsächlich gezahlt hatten, im Schnitt je Anfo. Grün hinterlegt, sobald das den vertretbaren Einkaufspreis je Anfo erreicht. Von Tag 60 auf Tag 90 sinkt der Wert meist leicht: Wer nach der ersten Rechnung kündigt, bekommt Geld per Rücklastschrift oder Gutschrift zurück. Graue Zellen: „noch offen“, solange Anfos des Monats das Alter noch nicht erreicht haben; „nicht vollständig gemessen“, wenn für einzelne Anfos der Zahlungsstand an dem Tag fehlt (gespeichert erst seit 15.05.2026) — diese Zellen werden nicht mehr voll."
      terms={[
        { label: TERMS.geldNachAlter.label, tip: TERMS.geldNachAlter.tip },
        { label: TERMS.cpaJeAnfoBestellung.label, tip: TERMS.cpaJeAnfoBestellung.tip },
        { label: TERMS.cpaBudgetErreicht.label, tip: TERMS.cpaBudgetErreicht.tip },
        { label: TERMS.umsatzNetto.label, tip: TERMS.umsatzNetto.tip },
        { label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip },
      ]}
      source="Täglich gespeicherter Zahlungsstand der PL-X-Bestellungen (seit 15.05.2026), Anfo-Werte (CPA-Wert bei Bestellung)"
      freshnessKey={['plx_orders', 'anfo_werte']}
    >
      <Box ref={messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : data.rows.length === 0 ? (
          <Hinweis>Noch kein Bestellmonat mit vollständig bekanntem Zahlungsstand.</Hinweis>
        ) : (
          <>
            <BefundZeile text={geldBefund(data.rows) ?? undefined} fehlt={geldBefund(data.rows) ? null : 'Noch kein Monat mit vollständigem Stand bei Tag 30 und 60.'} tip={TERMS.cpaBudgetErreicht.tip} />
            <Kartenkopf>
              Bestellmonate {formatMonat(data.rows[0].month)}–{formatMonat(data.rows[data.rows.length - 1].month)} · Zahlungsstand{' '}
              {spanne(data.snapshot_ab, data.snapshot_stand ?? data.snapshot_ab)} · ohne vorläufige Anfos
            </Kartenkopf>
            <SortableTable rows={zeilen} columns={spalten} rowKey={(r) => r.month} maxHeight={null} eng={schmal} />
            <Hinweis>
              Gemessen, nicht geschätzt: der Zahlungsstand jeder Anfo am Tag Bestellung + 30/60/90/180, wie PL-X ihn an diesem Tag gemeldet hat.
              Fehlt ein ganzer Tag
              {data.fehlende_tage.length > 0 ? ` (${data.fehlende_tage.map(datumDe).join(', ')})` : ''}, gilt der Vortag. Steht eine Anfo an einem
              Tag nicht im Zahlungsstand, hatte sie nichts gezahlt (0 €). Rückbuchungen senken den Wert, er kann mit dem Alter auch fallen. Netto über
              das Verhältnis brutto/netto der bezahlten Anfos des Produkts. Der Einkaufspreis ist eine Vorhersage des Anfo-Wert-Modells, nicht gegen
              echte Zahlungen geprüft.
              {ohneCpa > 0 ? ` ${int(ohneCpa)} Anfos ohne CPA-Wert sind aus dem Einkaufspreis herausgerechnet (nicht als 0 gezählt).` : ''}
              {data.unbeobachtbar_status > 0 ? ` ${int(data.unbeobachtbar_status)}-mal fehlte eine Anfo mit unklarem Vertragsstand — diese Zellen sind grau.` : ''}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default GeldNachAlterKarte;
